import * as fs from 'fs'
import * as path from 'path'
import { formatCode } from '../generators/utils.js'
import { Dependencies } from '../agent-project/types.js'

// This constant is injected at build time by esbuild's define
declare const __RUNTIME_VERSION__: string

export class AgentProjectGenerator {
  private projectPath: string
  private projectName: string
  private packageManager: string
  private template: string

  constructor(projectPath: string, packageManager: string = 'bun', template: string = 'blank') {
    this.projectPath = path.resolve(projectPath)
    this.projectName = path.basename(this.projectPath)
    this.packageManager = packageManager
    this.template = template
  }

  async generate(): Promise<void> {
    this.ensureEmptyDirectory()
    this.createPackageJson()
    await this.createAgentConfig()
    this.createTsConfig()
    this.createAgentJson()
    this.createGitIgnore()
    await this.createReadme()
    await this.createSourceStructure()
  }

  private ensureEmptyDirectory(): void {
    // Ensure directory exists
    if (!fs.existsSync(this.projectPath)) {
      fs.mkdirSync(this.projectPath, { recursive: true })
    }

    // Check if directory is empty
    const files = fs.readdirSync(this.projectPath)
    if (files.length > 0) {
      throw new Error(`Directory ${this.projectPath} is not empty. Please use an empty directory.`)
    }
  }

  private createPackageJson(): void {
    const packageJson = {
      name: this.projectName,
      version: '1.0.0',
      description: `A Botpress Agent built with the ADK`,
      type: 'module',
      packageManager: this.packageManager === 'npm' ? undefined : `${this.packageManager}@latest`,
      scripts: {
        dev: 'adk dev',
        build: 'adk build',
        deploy: 'adk deploy',
      },
      dependencies: {
        '@botpress/runtime': `^${__RUNTIME_VERSION__}`,
      },
      devDependencies: {
        typescript: '^5.9.3',
      },
    }

    // Remove packageManager field if undefined (for npm)
    if (packageJson.packageManager === undefined) {
      delete (packageJson as any).packageManager
    }

    this.writeJsonFile('package.json', packageJson)
  }

  private getDefaultDependencies(): Dependencies {
    const dependencies: Dependencies = {
      integrations: {},
    }

    // Add default integrations for hello-world template
    if (this.template === 'hello-world') {
      dependencies.integrations = {
        chat: {
          version: 'chat@latest',
          enabled: true,
        },
        webchat: {
          version: 'webchat@latest',
          enabled: true,
        },
      }
    }

    return dependencies
  }

  private async createAgentConfig(): Promise<void> {
    const dependencies = this.getDefaultDependencies()
    const integrationsJson = JSON.stringify(dependencies.integrations, null, 4).replace(/\n/g, '\n    ')

    const defaultModels =
      this.template === 'hello-world'
        ? `\n  defaultModels: {
    autonomous: "cerebras:gpt-oss-120b",
    zai: "cerebras:gpt-oss-120b",
  },\n`
        : ''

    const agentConfig = `import { z, defineConfig } from '@botpress/runtime';

export default defineConfig({
  name: '${this.projectName}',
  description: 'An AI agent built with Botpress ADK',
${defaultModels}
  bot: {
    state: z.object({}),
  },

  user: {
    state: z.object({}),
  },

  dependencies: {
    integrations: ${integrationsJson},
  },
});
`

    await this.writeFormattedFile('agent.config.ts', agentConfig)
  }

  private createTsConfig(): void {
    const tsConfig = {
      compilerOptions: {
        target: 'ES2022',
        module: 'ES2022',
        moduleResolution: 'Bundler',
        lib: ['ES2022'],
        outDir: './dist',
        rootDir: '.',
        strict: true,
        esModuleInterop: true,
        allowSyntheticDefaultImports: true,
        skipLibCheck: true,
        forceConsistentCasingInFileNames: true,
        moduleDetection: 'force',
        resolveJsonModule: true,
        paths: {
          '@botpress/runtime/_types/*': ['./.adk/*-types'],
        },
      },
      include: ['src/**/*', '.adk/**/*'],
      exclude: ['node_modules', 'dist'],
    }

    this.writeJsonFile('tsconfig.json', tsConfig)
  }

  private createAgentJson(): void {
    const agentJson = {}
    this.writeJsonFile('agent.json', agentJson)
  }

  private createGitIgnore(): void {
    const gitIgnore = `# Dependencies
node_modules/
.pnpm-store/

# Build outputs
dist/
.adk/

# Environment files
.env
.env.local
.env.production

# IDE files
.vscode/
.idea/
*.swp
*.swo

# OS files
.DS_Store
Thumbs.db

# Logs
*.log
logs/

# Runtime files
*.pid
*.seed
*.pid.lock
`

    this.writeFile('.gitignore', gitIgnore)
  }

  private async createReadme(): Promise<void> {
    const installCommand =
      this.packageManager === 'npm'
        ? 'npm install'
        : this.packageManager === 'yarn'
          ? 'yarn install'
          : `${this.packageManager} install`

    const readme = `# ${this.projectName}

A Botpress Agent built with the ADK.

## Getting Started

1. Install dependencies:
   \`\`\`bash
   ${installCommand}
   \`\`\`

2. Start development server:
   \`\`\`bash
   adk dev
   \`\`\`

3. Deploy your agent:
   \`\`\`bash
   adk deploy
   \`\`\`

## Project Structure

- \`src/actions/\` - Define callable functions
- \`src/workflows/\` - Define long-running processes
- \`src/conversations/\` - Define conversation handlers
- \`src/tables/\` - Define data storage schemas
- \`src/triggers/\` - Define event subscriptions
- \`src/knowledge/\` - Add knowledge base files

## Learn More

- [ADK Documentation](https://botpress.com/docs/adk)
- [Botpress Platform](https://botpress.com)
`

    await this.writeFormattedFile('README.md', readme)
  }

  private async createSourceStructure(): Promise<void> {
    const srcPath = path.join(this.projectPath, 'src')
    fs.mkdirSync(srcPath)

    const subdirectories = ['actions', 'conversations', 'knowledge', 'tables', 'triggers', 'workflows']

    for (const subdir of subdirectories) {
      const subdirPath = path.join(srcPath, subdir)
      fs.mkdirSync(subdirPath)

      // Special handling for conversations directory in hello-world template
      if (subdir === 'conversations' && this.template === 'hello-world') {
        const conversationContent = `import { Conversation } from "@botpress/runtime";

export default new Conversation({
  channel: "*",
  handler: async ({ execute }) => {
    await execute({
      instructions: \`You are a helpful AI assistant built with Botpress ADK. You can assist users with their questions and tasks.\`,
    });
  },
});
`
        await this.writeFormattedFile(path.join('src', subdir, 'index.ts'), conversationContent)
      } else {
        const placeholderContent = `/**
 * TODO: Add your ${subdir} here
 *
 * This is a placeholder file to initialize the ${subdir} directory.
 * You can delete this file once you add your own ${subdir}.
 */

export default {};
`

        await this.writeFormattedFile(path.join('src', subdir, 'index.ts'), placeholderContent)
      }
    }
  }

  private writeFile(relativePath: string, content: string): void {
    const filePath = path.join(this.projectPath, relativePath)
    fs.writeFileSync(filePath, content)
  }

  private async writeFormattedFile(relativePath: string, content: string): Promise<void> {
    const formatted = await formatCode(content, relativePath)
    this.writeFile(relativePath, formatted)
  }

  private writeJsonFile(relativePath: string, data: any): void {
    this.writeFile(relativePath, JSON.stringify(data, null, 2) + '\n')
  }
}
